import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { LPP_COUNTRY_CODES } from "../data/regions";
import { ApplicationState } from "src/store";
import { UserState } from "src/store/reducers/user-reducer";
import { EUROPE } from "../constants/privacyPolicyConstants";
import { PP_LANGUAGE, PP_REGION } from "../constants/storage";
import { Cookie } from "../services/storage";
import { PrivacyPolicyTabType, TabItem } from "../utils/privacyPolicyUtils";
import { getCountryDisplayName } from "../utils/regionUtils";

interface UsePrivacyPolicyTabsReturn {
  activeTab: string;
  activeTabType: PrivacyPolicyTabType;
  shouldShowTabs: boolean;
  hasLPP: boolean;
  tabItem: TabItem[];
  handleTabChange: (tabName: string) => void;
}

/**
 * Custom hook for managing privacy policy tabs (EU countries only)
 * Handles tab state, EU detection, and tab switching logic
 */
export const usePrivacyPolicyTabs = (): UsePrivacyPolicyTabsReturn => {
  const cookiePPRegion = Cookie.get(PP_REGION);
  const cookiePPLanguage = Cookie.get(PP_LANGUAGE);

  // Get the selected privacy policy language display name
  const selectedPPLanguage = useMemo(() => {
    if (cookiePPRegion?.toLowerCase() === EUROPE && cookiePPLanguage) {
      return getCountryDisplayName(
        cookiePPRegion,
        cookiePPLanguage.split("-")[1]
      );
    }
    return "";
  }, [cookiePPRegion, cookiePPLanguage]);

  // Tab items for EU countries
  const tabItem: TabItem[] = useMemo(
    () => [{ name: "Global" }, { name: selectedPPLanguage }],
    [selectedPPLanguage]
  );

  // Extract country code from cookie
  const cookieCountryCode = cookiePPLanguage
    ? cookiePPLanguage.split("-")[1]
    : "";

  // Check if country is EU (excluding Turkey)
  const hasLPP = LPP_COUNTRY_CODES.includes(cookieCountryCode);

  // Determine if tabs should be shown (EU country excluding Turkey)
  const shouldShowTabs = useMemo(() => {
    return hasLPP;
  }, [cookiePPRegion, hasLPP]);

  // Active tab state - default to Global tab for EU countries
  const [activeTab, setActiveTab] = useState<string>("Global");
  const [activeTabType, setActiveTabType] =
    useState<PrivacyPolicyTabType>("global");

  // Update active tab when selectedPPLanguage changes (e.g., when user changes language)
  // Default to Global tab when language changes for EU countries
  useEffect(() => {
    if (selectedPPLanguage && shouldShowTabs) {
      setActiveTab("Global");
      setActiveTabType("global");
    }
  }, [selectedPPLanguage, shouldShowTabs]);

  // Handle tab change
  const handleTabChange = useCallback((tabName: string) => {
    const newTabType = tabName === "Global" ? "global" : "local";
    setActiveTab(tabName);
    setActiveTabType(newTabType);
  }, []);

  return {
    activeTab,
    activeTabType,
    shouldShowTabs,
    hasLPP,
    tabItem,
    handleTabChange,
  };
};
